// Steps 4 to 6 of a batch: the preview of every ticked project, the import as
// it runs, and the report. The batch on the server says which one shows.
import { useState } from "react"
import { Badge, Button, ErrorNote, useToast } from "../../ui"
import { api } from "../../../lib/api"
import {
  type Batch,
  type BatchItem,
  type ShelfPreview,
  type ShelfSummary,
  type TargetCompany,
  blockers,
  companyLabel,
  groupByCustomer,
  previewTotals,
  shelfTotals,
} from "../../../lib/batchMigrations"
import { formatBytes, formatCount } from "../../../lib/migrations"
import { NotReimportedList } from "../../NotReimportedList"
import { Bar, ItemList, Note, Stat, StepLayout } from "../parts"

const base = (batch: Batch) => `/platform/migrations/batches/${batch.id}`
const isBookstack = (batch: Pick<Batch, "source">) => batch.source === "bookstack"

/** While the server works: what it is doing, and how far it is. */
export function BatchWorking({ batch, title, intro }: { batch: Batch; title: string; intro: string }) {
  const p = batch.progress
  return (
    <StepLayout title={title} intro={intro} guide={{ points: ["You can leave this page; the work carries on and this page picks it up again."] }}>
      <BatchProgress batch={batch} />
      {!p && <p className="text-sm text-muted">Starting…</p>}
    </StepLayout>
  )
}

function BatchProgress({ batch }: { batch: Batch }) {
  const p = batch.progress
  if (!p) return null
  return (
    <div className="flex flex-col gap-3">
      {p.items && p.items.total > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-foreground">
            {isBookstack(batch) ? "Shelf" : "Project"} {formatCount(Math.min(p.items.done + 1, p.items.total))} of {formatCount(p.items.total)}
            {p.items.item && <span className="font-mono text-muted"> · {p.items.item}</span>}
          </p>
          <Bar percent={(p.items.done / p.items.total) * 100} />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <p className="text-xs text-muted">
          {p.label}
          {p.total > 0 && ` — ${formatCount(p.done)} of ${formatCount(p.total)}`}
        </p>
        {p.total > 0 && <Bar percent={(p.done / p.total) * 100} />}
      </div>
    </div>
  )
}

export function BatchPreviewStep({
  batch,
  companies,
  onChange,
  onCancel,
}: {
  batch: Batch
  companies: TargetCompany[]
  onChange: (b: Batch) => void
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const bookstack = isBookstack(batch)
  // Each source's previews have their own shape: a shelf's has no counts, so
  // the YouTrack totals must never be summed over one.
  const totals = bookstack ? null : previewTotals(batch.items)
  const shelves = bookstack ? shelfTotals(batch.items) : null
  const blocked = blockers(batch.items)

  const call = async (fn: () => Promise<{ batch: Batch }>) => {
    setBusy(true)
    setError(null)
    try {
      onChange((await fn()).batch)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <StepLayout
      title="Preview"
      intro={bookstack ? "Every ticked shelf, counted and checked against its space without writing anything." : "Every ticked project, planned against its target without writing anything."}
      guide={bookstack ? {
        points: [
          "Pages are counted as BookStack reports them; drafts are skipped on import.",
          "Imported pages are browsable at once. Embedding them for search is a separate, billed step on each space.",
          "Starting creates the new companies and spaces first, then imports one shelf at a time.",
        ],
      } : {
        points: [
          "Counts are what the import would write now; attachments are counted by the size YouTrack reports.",
          "A project with a problem cannot start. Untick it to go on without it, or go back and change its target.",
          "Starting creates the new companies and projects first, then imports one project at a time.",
        ],
      }}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel batch
          </Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => call(() => api.patch(`${base(batch)}/items`, { items: [] }))}>
            Back to selection
          </Button>
          <Button
            type="button"
            className="ml-auto"
            disabled={busy || blocked.length > 0 || !(shelves?.shelves ?? totals?.projects)}
            onClick={() => call(() => api.post(`${base(batch)}/start`))}
          >
            Start import
          </Button>
        </>
      }
    >
      {shelves && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="Shelves" value={formatCount(shelves.shelves)} />
          <Stat label="Books" value={formatCount(shelves.books)} />
          <Stat label="Pages" value={formatCount(shelves.pages)} />
          <Stat label="Companies" value={formatCount(shelves.companies)} />
        </div>
      )}
      {totals && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Projects" value={formatCount(totals.projects)} />
            <Stat label="Items" value={formatCount(totals.items)} />
            <Stat label="Sprints" value={formatCount(totals.sprints)} />
            <Stat label="Attachments" value={formatBytes(totals.attachmentBytes)} />
          </div>
          <p className="text-xs text-muted">
            People: {formatCount(totals.peopleLinked)} linked by email, {formatCount(totals.peopleCreated)} new and disabled.
          </p>
        </>
      )}
      <ErrorNote error={error} />
      {blocked.length > 0 && (
        <Note tone="danger">
          {formatCount(blocked.length)} {bookstack ? (blocked.length === 1 ? "shelf" : "shelves") : blocked.length === 1 ? "project" : "projects"} cannot start yet:{" "}
          {blocked.map((i) => i.sourceProject.key).join(", ")}.
        </Note>
      )}
      {groupByCustomer(batch.items.filter((i) => i.selected)).map((g) => (
        <section key={g.customer} className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            {g.customer} <span className="font-normal text-muted">→ {companyLabel(g.items[0]!.companyTarget, companies)}</span>
          </h3>
          {g.items.map((item) => bookstack ? (
            <ShelfPreviewRow
              key={item.id}
              item={item}
              disabled={busy}
              onUntick={() => call(() => api.patch(`${base(batch)}/items`, { items: [{ id: item.id, selected: false }] }))}
            />
          ) : (
            <PreviewRow
              key={item.id}
              item={item}
              disabled={busy}
              onUntick={() => call(() => api.patch(`${base(batch)}/items`, { items: [{ id: item.id, selected: false }] }))}
            />
          ))}
        </section>
      ))}
    </StepLayout>
  )
}

function targetText(item: BatchItem) {
  const t = item.projectTarget
  if (!t) return "no target"
  return "new" in t ? `new project ${t.new.keyPrefix}` : "an existing project"
}

function PreviewRow({ item, disabled, onUntick }: { item: BatchItem; disabled: boolean; onUntick: () => void }) {
  const p = item.preview
  const problems = p?.problems ?? []
  return (
    <div className={`rounded-md border p-3 ${problems.length || !p ? "border-danger/40" : "border-border"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted">{item.sourceProject.key}</span>
        <span className="text-sm text-foreground">{item.sourceProject.name}</span>
        <span className="text-xs text-muted">→ {targetText(item)}</span>
        {(problems.length > 0 || !p) && (
          <Button type="button" size="sm" variant="ghost" className="ml-auto" disabled={disabled} onClick={onUntick}>
            Leave it out
          </Button>
        )}
      </div>
      {!p ? (
        <p className="mt-2 text-sm text-danger">{item.error ?? "Not previewed."}</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-muted">
            {formatCount(p.counts.items)} items ({formatCount(p.counts.epics)} epics, {formatCount(p.counts.tasks)} tasks,{" "}
            {formatCount(p.counts.subtasks)} sub-tasks) · {formatCount(p.counts.sprints)} sprints · {formatCount(p.counts.comments)}{" "}
            comments · {formatBytes(p.addBytes)} attachments · people {p.people.linked} linked, {p.people.created} new,{" "}
            {p.people.system} system
          </p>
          <p className="text-xs text-muted">
            Board: {p.board.statuses} statuses, {p.board.types} types, {p.board.priorities} priorities, {p.board.fields} fields,{" "}
            {p.board.labels} labels{p.keepSourceNumbers ? " · keeps YouTrack's numbers" : ""}
          </p>
          {problems.map((x) => (
            <p key={x} className="mt-1 text-sm text-danger">
              {x}
            </p>
          ))}
          {p.board.created.length > 0 && (
            <details className="mt-1 text-xs">
              <summary className="cursor-pointer text-muted">{formatCount(p.board.created.length)} values created on the board</summary>
              <p className="mt-1 text-foreground">{p.board.created.join(", ")}</p>
            </details>
          )}
          {p.warnings.length > 0 && (
            <details className="mt-1 text-xs">
              <summary className="cursor-pointer text-warning">{formatCount(p.warnings.length)} warnings</summary>
              <ItemList items={p.warnings.slice(0, 50).map((w) => ({ what: "warning", detail: w }))} />
            </details>
          )}
        </>
      )}
    </div>
  )
}

export function BatchImportStep({ batch, onChange }: { batch: Batch; onChange: (b: Batch) => void }) {
  const [error, setError] = useState<unknown>(null)
  const cancel = async () => {
    setError(null)
    try {
      onChange((await api.post<{ batch: Batch }>(`${base(batch)}/cancel`)).batch)
    } catch (err) {
      setError(err)
    }
  }
  return (
    <StepLayout
      title="Importing"
      intro={
        isBookstack(batch)
          ? "Shelves are imported one at a time. A shelf that fails is reported, and the others carry on."
          : "Projects are imported one at a time. A project that fails is reported, and the others carry on."
      }
      guide={isBookstack(batch) ? {
        points: [
          "For each shelf: its company and space are created if needed, the token is stored on the space, then every page of its books is read and images copied.",
          "Nobody is emailed about anything the import writes.",
        ],
        tip: "Cancelling stops after the current shelf. What was written stays; a new batch tops it up.",
      } : {
        points: [
          "For each project: its company and project are created if needed, attachments downloaded, then rows written and files uploaded.",
          "Nobody is emailed about anything the import writes.",
        ],
        tip: "Cancelling stops after the current step. What was written stays; a new batch tops it up.",
      }}
      footer={
        <Button type="button" variant="danger" onClick={cancel}>
          Cancel
        </Button>
      }
    >
      <BatchProgress batch={batch} />
      <ErrorNote error={error} />
      <ul className="flex flex-col gap-1 text-sm">
        {batch.items
          .filter((i) => i.selected)
          .map((i) => (
            <li key={i.id} className="flex items-center gap-2">
              <ItemStatus item={i} />
              <span className="font-mono text-xs text-muted">{i.sourceProject.key}</span>
              <span className="text-foreground">{i.sourceProject.name}</span>
            </li>
          ))}
      </ul>
    </StepLayout>
  )
}

function ShelfPreviewRow({ item, disabled, onUntick }: { item: BatchItem; disabled: boolean; onUntick: () => void }) {
  const p = item.preview as unknown as ShelfPreview | null
  const problems = p?.problems ?? []
  const target = item.projectTarget as unknown as { spaceId: string } | { new: { name: string } } | null
  return (
    <div className={`rounded-md border p-3 ${problems.length || !p ? "border-danger/40" : "border-border"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground">{item.sourceProject.name}</span>
        <span className="text-xs text-muted">
          → {target && "new" in target ? `new space "${target.new.name}"` : `space "${p?.space ?? "existing"}"`}
        </span>
        {(problems.length > 0 || !p) && (
          <Button type="button" size="sm" variant="ghost" className="ml-auto" disabled={disabled} onClick={onUntick}>
            Leave it out
          </Button>
        )}
      </div>
      {!p ? (
        <p className="mt-2 text-sm text-danger">{item.error ?? "Not previewed."}</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-muted">
            {formatCount(p.books)} book{p.books === 1 ? "" : "s"} · {formatCount(p.pages)} page{p.pages === 1 ? "" : "s"}
          </p>
          {problems.map((x) => (
            <p key={x} className="mt-1 text-sm text-danger">
              {x}
            </p>
          ))}
          {p.warnings.map((w) => (
            <p key={w} className="mt-1 text-xs text-warning">
              {w}
            </p>
          ))}
        </>
      )}
    </div>
  )
}

function ItemStatus({ item }: { item: BatchItem }) {
  const tone = { completed: "success", failed: "danger", importing: "accent", previewed: "neutral", pending: "neutral" } as const
  const label = { completed: "done", failed: "failed", importing: "importing", previewed: "waiting", pending: "waiting" }
  return <Badge tone={tone[item.status]}>{label[item.status]}</Badge>
}

export function BatchReportStep({ batch, companies, onNew }: { batch: Batch; companies: TargetCompany[]; onNew: () => void }) {
  const toast = useToast()
  const done = batch.items.filter((i) => i.selected)
  const bookstack = isBookstack(batch)

  const downloadCsv = async (item: BatchItem, file: "keymap" | "not-reimported") => {
    try {
      const csv = await api.raw(`${base(batch)}/items/${item.id}/${file}.csv`)
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
      const a = document.createElement("a")
      a.href = url
      a.download = `${file}-${item.sourceProject.key}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast(file === "keymap" ? "Could not download the key map. Try again." : "Could not download the list. Try again.", "error")
    }
  }

  const title = { completed: "Batch finished", failed: "Batch failed", cancelled: "Batch cancelled" }[batch.status as "completed" | "failed" | "cancelled"] ?? "Batch"
  return (
    <StepLayout
      title={title}
      intro={batch.finishedAt ? `Ended ${new Date(batch.finishedAt).toLocaleString()}.` : ""}
      guide={{
        points: bookstack
          ? [
              "Each space keeps the token: \"Sync now\" on its page brings in what changed since.",
              "Pages are not embedded yet. Embed each space from its page when it should be searchable by meaning.",
            ]
          : [
              "New people were added disabled. Activate them on each company's Employees page.",
              "The key map of each project lists every YouTrack key and the key it has now.",
            ],
      }}
      footer={
        <Button type="button" className="ml-auto" onClick={onNew}>
          Start a new batch
        </Button>
      }
    >
      {batch.error && <Note tone={batch.status === "completed" ? "warning" : "danger"}>{batch.error}</Note>}
      {groupByCustomer(done).map((g) => (
        <section key={g.customer} className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            {g.customer} <span className="font-normal text-muted">→ {companyLabel(g.items[0]!.companyTarget, companies)}</span>
          </h3>
          {g.items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3 text-sm">
              <ItemStatus item={item} />
              <span className="font-mono text-xs text-muted">{item.sourceProject.key}</span>
              <span className="text-foreground">{item.sourceProject.name}</span>
              {item.summary && bookstack && <ShelfResult summary={item.summary as unknown as ShelfSummary} />}
              {item.summary && bookstack && ((item.summary as unknown as ShelfSummary).result.notReimported?.length ?? 0) > 0 && (
                <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={() => downloadCsv(item, "not-reimported")}>
                  Not re-imported (CSV)
                </Button>
              )}
              {item.summary && bookstack && (
                <div className="w-full">
                  <NotReimportedList
                    pages={(item.summary as unknown as ShelfSummary).result.notReimported}
                    omitted={(item.summary as unknown as ShelfSummary).result.notReimportedOmitted}
                  />
                </div>
              )}
              {item.summary && !bookstack && (
                <span className="text-xs text-muted">
                  {formatCount(item.summary.counts.items)} items · {formatCount(item.summary.counts.sprints)} sprints ·{" "}
                  {formatCount(item.summary.counts.comments)} comments · {formatCount(item.summary.newPeople.length)} new people
                </span>
              )}
              {item.status === "completed" && !bookstack && (
                <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={() => downloadCsv(item, "keymap")}>
                  Key map
                </Button>
              )}
              {item.error && <p className="w-full text-xs text-danger">{item.error}</p>}
            </div>
          ))}
        </section>
      ))}
    </StepLayout>
  )
}

function ShelfResult({ summary }: { summary: ShelfSummary }) {
  const r = summary.result
  return (
    <span className="text-xs text-muted">
      space "{summary.spaceName}" · {formatCount(r.pagesCreated)} added, {formatCount(r.pagesUpdated)} updated,{" "}
      {formatCount(r.pagesUnchanged)} unchanged
      {r.pagesRefused > 0 && `, ${formatCount(r.pagesRefused)} refused`}
      {r.imagesImported > 0 && ` · ${formatCount(r.imagesImported)} images copied`}
      {r.imagesFailed > 0 && `, ${formatCount(r.imagesFailed)} not copied`}
      {r.errors.length > 0 && ` · ${formatCount(r.errors.length)} problem(s)`}
    </span>
  )
}
